import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import NetworkSpinner from './ui/NetworkSpinner';

const ProtectedRoute = ({
    children,
    requireOrganization = false,
    requireAdmin = false,
    requireManager = false,
    requireAppAccess = false,
}) => {
    const { user, profile, loading, organizationId, isAdmin, isManager, impersonating } = useAuth();
    // Bug #9 — timeout di 15s per evitare loading infinito
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        if (!loading) { setTimedOut(false); return; }
        const t = setTimeout(() => setTimedOut(true), 15000);
        return () => clearTimeout(t);
    }, [loading]);

    if (loading) {
        if (timedOut) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#030014' }}>
                    <AlertTriangle className="w-8 h-8 text-amber-400" />
                    <p className="text-white/70 text-sm">Impossibile caricare il profilo.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                        style={{ background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.4)' }}
                    >
                        <RefreshCw className="w-4 h-4" /> Ricarica
                    </button>
                </div>
            );
        }
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: '#030014' }}>
                <div className="flex flex-col items-center gap-3">
                    <NetworkSpinner size={40} />
                    <span className="text-violet-300/60 text-xs font-mono tracking-widest uppercase">Caricamento...</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Orphan Guard: redirect to onboarding if no organization.
    // ONLY redirect if profile is loaded AND organization_id is null.
    // ⚠️ Demo accounts (role='demo') bypass this check — they have no org by design.
    // ⚠️ Admin/Manager bypass this check — they don't necessarily have an org.
    if (requireOrganization && profile && !organizationId && profile.role !== 'demo' && !isAdmin && !isManager) {
        return <Navigate to="/onboarding" replace />;
    }

    // Admin guard: only admin/superadmin can access /admin/*
    if (requireAdmin && profile && !isAdmin) {
        return <Navigate to={isManager ? '/manager/overview' : '/app/dashboard'} replace />;
    }

    // Manager guard: only managers can access /manager/*
    // Exception 1: admins can access when NOT impersonating (they browse freely)
    // Exception 2: admins CAN access when impersonating a manager
    // Block: admin NOT impersonating (or impersonating non-manager) → /admin/overview
    const isImpersonatingManager = impersonating && impersonating.userProfile?.role === 'manager';
    if (requireManager && profile) {
        // Non-admin, non-manager user → send to app dashboard
        if (!isManager && !isAdmin) {
            return <Navigate to="/app/dashboard" replace />;
        }
        // Admin NOT impersonating a manager → redirect back to admin console
        if (isAdmin && !isManager && !isImpersonatingManager) {
            return <Navigate to="/admin/overview" replace />;
        }
    }

    // App guard: prevent Managers and Admins from accessing agent/app dashboard
    // unless they are actively impersonating an operational (non-manager) user.
    if (requireAppAccess && profile && (isAdmin || isManager) && !impersonating) {
        return <Navigate to={isAdmin ? '/admin/overview' : '/manager/overview'} replace />;
    }
    // Edge case: admin is impersonating a manager but landed on /app/* anyway
    // → redirect to the manager console instead of showing the agency layout.
    if (requireAppAccess && impersonating && impersonating.userProfile?.role === 'manager') {
        return <Navigate to="/manager/overview" replace />;
    }

    return children;
};

export default ProtectedRoute;
